/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {Dispatch} from 'redux'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {useDispatch} from 'react-redux'
import {KTSVG} from '../../../helpers'
import {getCSSVariableValue} from '../../../assets/ts/_utils'
import {Dropdown1} from '../..'
import {Card} from '../../../../app/pages/dashboard/partials/Card'
import moment from 'moment'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import {
  CheckAccess,
  compareTwoObj,
  convertUTCToLocal,
  downloadExcel,
  handleNotificationWithToastify,
  IsCompanyAdmin,
  IsRootUserOrAdmin,
} from '../../../../app/services/Functional'
import {setActiveTab} from '../../../../setup/redux/tabs/slices'
import FAIcon from '../../../helpers/components/FAIcon'
import {faDownload} from '@fortawesome/free-solid-svg-icons'

type Props = {
  className: string
  chartColor: string
  strokeColor: string
  chartHeight: string
  tcc_balance: number
  statistics: any
}

const MixedWidget2: React.FC<Props> = ({
  className,
  chartColor,
  chartHeight,
  strokeColor,
  statistics,
  tcc_balance,
}) => {
  const navigate = useNavigate()
  const dispatch: Dispatch<any> = useDispatch()
  const chartRef = useRef<HTMLDivElement | null>(null)
  const [isVisibleDashboardBox, setIsVisibleDashboardBox] = useState(true)
  const isSuperAdminUser = IsRootUserOrAdmin()
  const iscompanyAdminUser = IsCompanyAdmin()
  const {user, inspection_companies}: any = useSelector<RootState>(
    (state) => ({
      user: state.auth.user,
      inspection_companies: state.auth.inspection_companies,
    }),
    shallowEqual
  )

  const active_inspection_company: any = useSelector<RootState>(
    ({auth}) => auth.active_inspection_company,
    shallowEqual
  )

  useEffect(() => {
    if (isSuperAdminUser) {
      const isSameObj = compareTwoObj(inspection_companies[0], active_inspection_company)
      setIsVisibleDashboardBox(isSameObj)
    }
  }, [active_inspection_company, inspection_companies])

  const showTransactionData = CheckAccess('transaction.index')

  useEffect(() => {
    if (!chartRef.current) {
      return
    }

    const chart = new ApexCharts(
      chartRef.current,
      chartOptions(chartHeight, chartColor, strokeColor, statistics)
    )
    if (chart) {
      chart.render()
    }

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef])

  const formatedDataForExcel = (data: any) => {
    const dataArray = [
      ['Sr. No.', 'Name', 'Count'],
      [1, 'Projects', data.projects],
      [2, 'Sites', data.sites],
      [3, 'Assets', data.assets],
      [4, 'Completed Activities', data.completed_inspections],
      [5, 'Pending Activities', data.pending_inspections],
      [6, `${active_inspection_company.slug === 'decent' ? 'TCC Credits' : 'Credit Balance (TCCs)'}`, tcc_balance],
      [7, 'QR Codes', data.remaining_qr_codes],
      // [8, 'NFCs', data.nfcOrders],
    ]

    if (user && isSuperAdminUser && isVisibleDashboardBox) {
      dataArray.push([8, 'Users', data.users])
      dataArray.push([9, 'Companies', data.companies])
    }

    if ((user && iscompanyAdminUser && isVisibleDashboardBox) || !isVisibleDashboardBox) {
      dataArray.push([8, 'Team Members', data.users])
    }

    if (!showTransactionData) {
      dataArray.splice(6, 1)
      for (let i = 1; i < dataArray.length; i++) {
        dataArray[i][0] = i
      }
    }

    return dataArray
  }

  const [loadingDownload, setLoadingDownload] = useState(false)

  const onClickConvertDataToExcel = () => {
    try {
      const formatedDataExcel = formatedDataForExcel(statistics?.cardStatistics)
      setLoadingDownload(false)
      handleNotificationWithToastify({
        success: true,
        message: 'You have successfully downloaded!',
      })
      return downloadExcel(formatedDataExcel, 'Analytics Report')
    } catch (error) {
      setLoadingDownload(false)
      handleNotificationWithToastify({
        success: false,
        message: 'Unable to download the file successfully!',
      })
      return error
    }
  }

  const handleTCCClick = (event: any) => {
    event.preventDefault()
    if (isSuperAdminUser) {
      dispatch(setActiveTab({module: 'Orders', index: 1}))
      navigate('/manage-orders')
    } else {
      dispatch(setActiveTab({module: 'Orders', index: 0}))
      navigate('/your-credits')
    }
  }

  return (
    <div className={`card ${className}`}>
      <div className={`card-header border-0 py-5 bg-${chartColor}`}>
        <h3 className='card-title fw-bolder text-white'>
          Completed Activities - {moment().format('MMMM YYYY')}
        </h3>
        <div className='card-toolbar'>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-color-white btn-active-white btn-active-color- border-0 me-n3'
            data-kt-menu-trigger='click'
            data-kt-menu-placement='bottom-end'
            data-kt-menu-flip='top-end'
          >
            <KTSVG path='/media/icons/duotune/general/gen024.svg' className='svg-icon-2' />
          </button>
          <Dropdown1 />
        </div>
      </div>

      <div className='card-body p-0'>
        <div
          ref={chartRef}
          className={`mixed-widget-2-chart card-rounded-bottom pb-20 bg-${chartColor}`}
        />

        <div className='card-p mt-n20 position-relative'>
          <div className='row g-0 justify-content-center'>
            <Card
              link='/projects'
              title='Projects'
              icon='/media/icons/duotune/general/Project Set-up.svg'
              value={statistics ? statistics?.cardStatistics.projects : '0'}
            />
            <Card
              link='/sites'
              title='Sites'
              icon='/media/icons/duotune/general/sites.svg'
              value={statistics ? statistics?.cardStatistics?.sites : '0'}
            />
            <Card
              link='/assets'
              title='Assets'
              icon='/media/icons/duotune/general/assets-mapping.svg'
              value={statistics ? statistics?.cardStatistics?.assets : '0'}
            />
            {user && isSuperAdminUser && active_inspection_company.slug === 'decent' ? (
              <>
                <Card
                  link='/team-management'
                  title='Users'
                  icon='/media/icons/duotune/general/Team Management.svg'
                  value={statistics ? statistics.cardStatistics?.users : '0'}
                />
                <Card
                  link='/inspection-companies'
                  title='Companies'
                  icon='/media/icons/duotune/general/company-listing.svg'
                  value={statistics ? statistics?.cardStatistics?.companies : '0'}
                />
              </>
            ) : null}
            {CheckAccess('transaction.index') && (
              <Card
                title={`${active_inspection_company.slug === 'decent' ? 'TCC Credits' : 'Credit Balance (TCCs)'}`}
                icon='/media/icons/duotune/general/Orders.svg'
                value={tcc_balance ? tcc_balance : '0'}
                onClick={handleTCCClick}
              />
            )}
            <Card
              link='/inspection-listing?inspection_status=complete'
              title='Completed Activities'
              icon='/media/icons/duotune/general/h&s-activity.svg'
              value={statistics ? statistics?.cardStatistics?.completed_inspections : '0'}
            />
            {(user && iscompanyAdminUser && isVisibleDashboardBox) || !isVisibleDashboardBox ? (
              <>
                <Card
                  title='Team Members'
                  link='/team-management'
                  icon='/media/icons/duotune/general/Team Management.svg'
                  value={statistics ? statistics.cardStatistics?.users : '0'}
                />
                <Card
                  title='Subscription Start Date'
                  icon='/media/icons/duotune/general/calendar.svg'
                  isLinkAvailable={false}
                  value={
                    active_inspection_company?.subscription_start_date_utc
                      ? moment(convertUTCToLocal(active_inspection_company?.subscription_start_date_utc)).format(
                          'D MMM YYYY'
                        )
                      : '-'
                  }
                />
                <Card
                  title='Subscription End Date'
                  isLinkAvailable={false}
                  icon='/media/icons/duotune/general/calendar.svg'
                  value={
                    active_inspection_company?.subscription_end_date_utc
                      ? moment(convertUTCToLocal(active_inspection_company?.subscription_end_date_utc)).format(
                          'D MMM YYYY'
                        )
                      : '-'
                  }
                />
              </>
            ) : null}
            <Card
              link='/inspection-listing?inspection_status=pending'
              title='Pending Activities'
              icon='/media/icons/duotune/general/h&s-activity.svg'
              value={statistics ? statistics?.cardStatistics?.pending_inspections : '0'}
            />
            <Card
              title='QR Code Balance'
              isLinkAvailable={false}
              icon='/media/icons/duotune/general/qrcode.svg'
              value={statistics ? statistics?.cardStatistics?.remaining_qr_codes : '0'}
            />
            {/* <Card
              title='NFC Orders'
              isLinkAvailable={false}
              icon='/media/icons/duotune/general/nfc.svg'
              value={statistics ? statistics?.cardStatistics?.nfcOrders : '0'}
            /> */}
          </div>
          <div className='g-0 d-flex justify-content-between data-label'>
            <span>*Data up to current date.</span>
            <OverlayTrigger
              key='show'
              placement='top'
              overlay={<Tooltip id='show'>Download report</Tooltip>}
            >
              <button
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm mx-3'
                onClick={onClickConvertDataToExcel}
                disabled={loadingDownload}
              >
                <FAIcon icon={faDownload} className='font-size-rem-1 text-primary fa-2x' />
              </button>
            </OverlayTrigger>
          </div>
        </div>
      </div>
    </div>
  )
}

const chartOptions = (
  chartHeight: string,
  chartColor: string,
  strokeColor: string,
  statistics: any
): ApexOptions => {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const color = getCSSVariableValue('--bs-' + chartColor)

  return {
    series: [
      {
        name: 'H&S Activity',
        data: statistics ? statistics.dailyInspections : [],
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: chartHeight,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      sparkline: {
        enabled: true,
      },
      dropShadow: {
        enabled: false,
        enabledOnSeries: undefined,
        top: 5,
        left: 0,
        blur: 3,
        color: strokeColor,
        opacity: 0.5,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 0,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [strokeColor],
    },
    xaxis: {
      categories: [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
        26, 27, 28, 29, 30, 31,
      ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: false,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        show: false,
        position: 'front',
        stroke: {
          color: borderColor,
          width: 1,
          dashArray: 3,
        },
      },
    },
    yaxis: {
      min: 0,
      max: 80,
      labels: {
        show: false,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      x: {
        formatter: function (val) {
          return moment().date(val).format('D MMM')
        },
      },
      y: {
        formatter: function (val) {
          return val + ''
        },
      },
      marker: {
        show: false,
      },
    },
    colors: ['transparent'],
    markers: {
      colors: [color],
      strokeColors: [strokeColor],
      strokeWidth: 3,
    },
  }
}

export {MixedWidget2}
